<template>
  <cs-view single-view class="excursion-builder">
    <template #content>
      <page-loading-animation :displayed="pageLoadingAnimationDisplayed()"></page-loading-animation>
      <cs-form-dialog
        id="dlg-student-medical-conditions-loading"
        v-model="studentMedicalConditionsLoadingAnimationDisplayed"
        body-id="dlg-body-student-medical-conditions-loading"
        heading="Retrieving Student Medical Conditions"
      >
        <template #cs-form-dialog-content>
          <div>
            Standby whilst we retrieve student medical data from your student information system.
            <br />This may take some time as we need to retrieve data for each individual student
            selected. <br />Please report any errors to our support team.
          </div>
          <div style="margin-top: 20px; text-align: center">
            <v-progress-circular
              :size="70"
              color="rgb(29, 175, 236)"
              class="loading-img"
              indeterminate
            ></v-progress-circular>
          </div>
        </template>
      </cs-form-dialog>
      <div v-show="!pageLoadingAnimationDisplayed()" class="excursion-builder-content">
        <v-row
          v-show="clientLogoMarginDisplayed()"
          no-gutters
          :class="vendorClientLogoEnabled ? 'mt-10' : 'mt-2'"
        >
        </v-row>
        <v-row v-show="vendorClientLogoEnabled && isAuthenticated" no-gutters>
          <v-col class="client-logo">
            <img
              id="clientLogo"
              alt="clientLogo"
              :style="{ background: clientLogoBackgroundColor }"
            />
          </v-col>
        </v-row>
        <v-card v-if="showError || !isAuthenticated" class="mt-10">
          <v-card-text class="ml-2">
            <v-row class="mb-20">
              <v-img max-width="280px" :src="stbHorizontalLogo"></v-img>
            </v-row>
            <v-row class="mt-2 mb-3">
              <v-col>
                You are not authorized to view Excursion Builder, please provide a correct auth
                token.
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <form
          v-if="!showError && isAuthenticated"
          id="form"
          :class="vendorClientLogoEnabled ? 'mt-10' : ''"
        >
          <v-row v-show="stbLogoDisplayed" no-gutters class="excursion-builder-title">
            <v-col>
              <v-img id="clientStepLogo" max-width="280px" :src="stbHorizontalLogo"></v-img>
            </v-col>
          </v-row>
          <v-row no-gutters class="excursion-builder-remark">
            <v-col class="excursion-builder-title-heading excursion-builder-i-content">
              <v-icon>fa:fas fa-info-circle</v-icon>
              Fill in your excursion details and complete checklists to produce your excursion risk
              assessment
            </v-col>
          </v-row>
          <!-- step 1: Excursion Details-->
          <v-row v-if="vendorExcursionSidInputEnabled" no-gutters class="flex-wrap">
            <v-col cols="12">
              <v-label class="form-label safe-trip-font">
                Excursion Manager ID
                <span class="text-red">&ast;</span>
              </v-label>
              <v-text-field
                id="excursion-builder--excursion-manager-id"
                v-model="vendorExcursionSid"
                variant="solo"
                placeholder="Excursion Manager ID"
                class="name-textbox excursion-input"
                :error-messages="vendorExcursionSidErrors"
                required
                :disabled="isExcursionInitiatedFromPcg"
                density="compact"
                @update:model-value="v$.vendorExcursionSid.$touch()"
                @blur="v$.vendorExcursionSid.$touch()"
              />
            </v-col>
          </v-row>
          <v-row no-gutters class="flex-wrap">
            <v-col cols="12">
              <v-label class="form-label safe-trip-font">
                Excursion Name
                <span class="text-red">&ast;</span>
              </v-label>
              <v-text-field
                id="excursion-builder--excursion-name"
                v-model="name"
                variant="solo"
                placeholder="Name"
                class="excursion-builder-input"
                :error-messages="nameErrors"
                required
                :disabled="
                  !formControlEnabled || archivedExcursionDetected || isExcursionInitiatedFromPcg
                "
                density="compact"
                @update:model-value="v$.name.$touch()"
                @blur="v$.name.$touch()"
              />
            </v-col>
          </v-row>
          <v-row no-gutters class="flex-wrap">
            <v-col cols="12">
              <v-label class="form-label safe-trip-font">
                Excursion Type
                <span class="text-red">&ast;</span>
              </v-label>
              <stb-icon-tooltip icon-color="rgb(119, 119, 119)" clickable>
                <div class="excursion-type-info">
                  <div
                    v-for="(excursionTypeInfoEntry, index) in excursionTypeInfoEntryList"
                    :key="index"
                  >
                    <b>{{ excursionTypeInfoEntry.label }}</b> -
                    {{ excursionTypeInfoEntry.description }}
                  </div>
                </div>
              </stb-icon-tooltip>
              <v-select
                id="excursion-builder--excursion-type"
                v-model="excursionType"
                class="type-dropdown excursion-selection"
                density="compact"
                :items="excursionTypeChoiceList"
                item-title="name"
                item-value="id"
                variant="solo"
                color="primary"
                placeholder="Choose an Excursion Type"
                :error-messages="selectErrors"
                required
                :menu-props="{
                  closeOnClick: false,
                  closeOnContentClick: false,
                  openOnClick: false,
                  maxHeight: 400
                }"
                :disabled="
                  !formControlEnabled || archivedExcursionDetected || isExcursionInitiatedFromPcg
                "
                :loading="excursionTypeLoaded"
                @blur="v$.excursionType.$touch()"
                @update:model-value="handleExcursionTypeSelectionChange()"
              >
                <template #item="data">
                  <v-list-item
                    v-bind="data.props"
                    :id="`excursion-builder--excursion-type--option-${String(data.item).replaceAll(
                      ' ',
                      ''
                    )}`"
                  />
                </template>
              </v-select>
            </v-col>
          </v-row>
          <v-row v-if="showJurisdiction" no-gutters class="flex-wrap">
            <v-col cols="12">
              <v-label class="form-label safe-trip-font">
                Excursion Destination
                <span class="text-red">&ast;</span>
              </v-label>
              <stb-icon-tooltip
                id="excursion-builder-destination-icon"
                icon-color="rgb(119, 119, 119)"
                clickable
              >
                <p class="py-2 mb-0">
                  {{ jurisdictionTooltip }}
                  Click
                  <a
                    id="excursion-builder--excursion-juristiction-tooltip--link"
                    href="https://complispace-jira.atlassian.net/wiki/spaces/CS/pages/448056/STB+Excursion+Details"
                    class="link-primary"
                    target="_blank"
                  >
                    here
                  </a>
                  to learn more.
                </p>
              </stb-icon-tooltip>
              <v-select
                id="excursion-builder--excursion-jurisdiction"
                v-model="jurisdiction"
                class="type-dropdown safe-trip-font excursion-selection safe-excursion-detail-text"
                :items="jurisdictions"
                variant="solo"
                :placeholder="'Choose an Excursion State'"
                :error-messages="selectErrors"
                required
                :menu-props="{
                  closeOnClick: false,
                  closeOnContentClick: false,
                  openOnClick: false,
                  maxHeight: 400
                }"
                :disabled="!formControlEnabled || archivedExcursionDetected"
                :loading="excursionJurisdictionLoaded"
                density="compact"
              >
                <template #item="data">
                  <v-list-item
                    v-bind="data.props"
                    :id="`excursion-builder--excursion-destination--option-${String(
                      data.item
                    ).replaceAll(' ', '')}`"
                  />
                </template>
              </v-select>
            </v-col>
          </v-row>
          <v-row
            v-if="excursionStartEndDateSetEnabled"
            no-gutters
            class="flex-wrap excursion-builder--excursion-date-picker-container"
          >
            <v-col cols="12">
              <date-selector
                id="excursion-builder--excursion-start-date-picker"
                v-model="excursionStartDate"
                label="Excursion Start Date"
                :allowed-dates="checkIfExcursionStartDateAllowed"
                :disabled="
                  !formControlEnabled || archivedExcursionDetected || isExcursionInitiatedFromPcg
                "
              ></date-selector>
            </v-col>
          </v-row>
          <v-row
            v-if="excursionStartEndDateSetEnabled"
            no-gutters
            class="flex-wrap excursion-builder--excursion-date-picker-container"
          >
            <v-col cols="12">
              <date-selector
                id="excursion-builder--excursion-end-date-picker"
                v-model="excursionEndDate"
                label="Excursion End Date"
                :allowed-dates="checkIfExcursionEndDateAllowed"
                :disabled="
                  !formControlEnabled ||
                  archivedExcursionDetected ||
                  disableDayExcursionEndDate ||
                  isExcursionInitiatedFromPcg
                "
              ></date-selector>
            </v-col>
          </v-row>
          <!-- step 2: Student Medical and Ratios-->
          <hr v-if="excursionType && excursionHighRiskStudentMedicalEnabled" class="mt-4 mb-4" />
          <div v-if="excursionType && excursionHighRiskStudentMedicalEnabled">
            <student-selection
              :form-control-enabled="formControlEnabled && !archivedExcursionDetected"
              @toggle="toggleStudentSelection"
              @save="saveChangesThenRevertToDraft(true)"
            ></student-selection>
            <risk-category
              :risk-categories="highRiskStudentMedicalConditionsRisks"
              :form-control-enabled="formControlEnabled && !archivedExcursionDetected"
              :save-draft="saveChangesThenRevertToDraft"
              @change-risk-category-done="uncheckExcursionConfirmation"
            />
            <staff-student-supervision
              :form-control-enabled="formControlEnabled && !archivedExcursionDetected"
              @toggle-supervision="toggleStaffStudentSupervision"
            ></staff-student-supervision>
            <v-row
              v-if="staffToStudentGenderRatioInfoEnabled && staffStudentSupervisionDisplayed"
              class="sub-category-section"
              no-gutters
            >
              <v-col cols="12" class="sub-label gender-ratios-label">
                <v-label class="form-label safe-trip-font">
                  <div>Supervisor & Student Gender Ratios (Optional)</div>
                </v-label>
                <v-textarea
                  id="student-gender-ratios"
                  ref="genderRatiosInput"
                  v-model="staffToStudentGenderRatioInfo"
                  no-resize="true"
                  variant="solo"
                  placeholder="Enter any additional numbers, ratios or clarification on gender ratios where required by school policies"
                  rows="1"
                  :disabled="!formControlEnabled || archivedExcursionDetected"
                  density="compact"
                ></v-textarea>
                <div
                  v-if="mutipleLineDetected"
                  id="excursion-builder--display-person-responsible-show-more--button"
                  class="person-responsible-show-more"
                  @click="
                    displayDetailsDialog(
                      'Supervisor & Student Gender Ratios',
                      staffToStudentGenderRatioInfo
                    )
                  "
                >
                  Show more
                </div>
              </v-col>
            </v-row>
          </div>
          <!-- step 3: Risk Categories-->
          <hr
            v-if="excursionType != '' && excursionType != null && excursionType != undefined"
            class="mt-4 mb-4"
          />
          <v-row v-if="excursionType">
            <v-col>
              <div v-if="providerUpdateActionDisplayed" class="update-provider-stepper">
                <v-row>
                  <v-col class="excursion-builder-i-content">
                    <v-icon>fa:fas fa-info-circle</v-icon>
                    <span>
                      Click the Update Providers button for any immediate updates that are required.
                    </span>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col class="update-provider-stepper-button">
                    <update-providers
                      :button-update-providers-enabled="
                        buttonUpdateProvidersEnabled && !archivedExcursionDetected
                      "
                      :form-control-enabled="formControlEnabled"
                    >
                    </update-providers>
                  </v-col>
                </v-row>
              </div>
              <v-row class="riskManifest">
                <v-col class="excursion-builder-i-content">
                  <v-icon>fa:fas fa-info-circle</v-icon>
                  <span> The SafeTripBuilder risk manifest is available </span>
                  <span class="riskManifestLink">
                    <span
                      id="excursion-builder--excursion-risk-manifest--link"
                      @click="registerRiskManifestLinkClickEvent()"
                      >here</span
                    >
                  </span>
                  <span> if you want to view all supported risk selections. </span>
                </v-col>
              </v-row>
              <risk-category
                :risk-categories="nonHighRiskStudentMedicalConditionsRisks"
                :form-control-enabled="formControlEnabled && !archivedExcursionDetected"
                :save-draft="saveChangesThenRevertToDraft"
                @change-risk-category-done="uncheckExcursionConfirmation"
              />
              <export-excursion-policy
                :excursion-policy-export-action-enabled="hasExcursion()"
                :submit-excursion="submit"
                :values-changed="valuesChanged"
                :excursion-builder-view-displayed="true"
              ></export-excursion-policy>
            </v-col>
          </v-row>
          <!-- step 4: Additional Information-->
          <hr class="mt-4 mb-4" />
          <v-row v-if="excursionType && excursionAdditionalInformationEnabled">
            <v-col>
              <v-row v-if="clientTemplate.sid" class="mb-4 flex-wrap">
                <v-col cols="12">
                  <v-label class="form-label safe-trip-font"> Pro Forma Template </v-label>
                  <div>
                    {{ clientTemplate.filename }}
                    <v-icon
                      :id="getDownloadFileBtnId(clientTemplate.filename)"
                      size="medium"
                      style="font-size: 15px; height: 17px; margin-left: 0.7%"
                      @click="downloadFile(clientTemplate)"
                      >fa:fa fa-download</v-icon
                    >
                  </div>
                </v-col>
              </v-row>
              <additional-info-list
                ref="additionalInfoList"
                :additional-information-items="additionalInformationItems"
                :form-control-enabled="formControlEnabled && !archivedExcursionDetected"
                @set-additional-information-valid="setAdditionalInformationValid"
              ></additional-info-list>
            </v-col>
          </v-row>
          <!-- step 5: Excursion Risk Assessment-->
          <hr class="mt-4 mb-4" />
          <v-row>
            <v-col class="main-title">
              {{ excursionStepperHeadingLabel.EXCURSION_RISK_ASSESSMENT }}
            </v-col>
          </v-row>
          <v-row id="riskOverallContainer" class="mt-3">
            <v-col>
              <risk-rating-overall risk-rating-refname="residual"></risk-rating-overall>
            </v-col>
          </v-row>
          <v-row v-if="excursionInherentRiskRatingDetected" id="inherentRiskContainer" class="mt-3">
            <v-col cols="12">
              <risk-rating-summary
                heading-label="Inherent Rating"
                :tooltip-bubble-text="inherentRiskRatingTooltipBubbleText"
                risk-rating-refname="inherent"
              ></risk-rating-summary>
            </v-col>
          </v-row>
          <v-row id="residualRiskContainer" class="mt-3">
            <v-col cols="12">
              <risk-rating-summary
                :heading-label="residualRiskRatingSummaryHeadingLabel"
                :tooltip-bubble-text="residualRiskRatingTooltipBubbleText"
                risk-rating-refname="residual"
              ></risk-rating-summary>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="12">
              <v-label v-if="riskAssessmentNotesDisplay" class="form-label safe-trip-font">
                Notes
              </v-label>
              <v-textarea
                id="risk-assessment-notes"
                v-model="riskAssessmentNotes"
                variant="solo"
                :no-resize="true"
                placeholder="Notes (optional)"
                rows="1"
                :disabled="!formControlEnabled || archivedExcursionDetected"
                density="compact"
              ></v-textarea>
            </v-col>
          </v-row>
          <v-row no-gutters align-start justify-start class="create-risk-assessment">
            <v-col cols="auto">
              <export-excursion-risk-assessment
                :excursion-policy-export-action-enabled="
                  isInvalid || !riskCategoriesValid || hasExcursion()
                "
                :register-risk-assessment-export-event="registerRiskAssessmentExportEvent"
                :submit-excursion="submit"
                :values-changed="valuesChanged"
              ></export-excursion-risk-assessment>
            </v-col>
          </v-row>
          <!-- step 6: Mandatory Excursion Checklists-->
          <hr v-if="excursionType" class="mt-4 mb-4" />
          <v-row v-if="excursionType" id="div" @click.stop="toggleMandatoryChecklist()">
            <v-col cols="12">
              <div class="mandatory-checklist excursion-builder-category-section">
                <v-icon>
                  {{ mandatoryChecklistDisplayed ? 'mdi-chevron-down' : 'mdi-chevron-left' }}
                </v-icon>
                <span class="name">Mandatory Excursion Checklists</span>
              </div>
            </v-col>
          </v-row>
          <div
            v-if="
              mandatoryChecklistBatchConfirmationAgreementEnabled && checklistAttestationEnabled
            "
            class="checkbox-container"
          >
            <v-checkbox
              id="`check-all-list`"
              v-model="mandatoryChecklistBatchConfirmationAgreement"
              color="primary"
              class="check-all-label"
              :checked="mandatoryChecklistBatchConfirmationAgreement"
              testing-id="mandatoryChecklistBatchConfirmationAgreementCheckbox"
              :disabled="archivedExcursionDetected"
              hide-details
            ></v-checkbox>
            <span class="checkAllLabel" for="`check-all-list`">
              I agree I have completed the following checklists
            </span>
          </div>
          <div v-if="excursionType" :hidden="!mandatoryChecklistDisplayed">
            <mandatory-checklist
              :save-draft="saveChangesThenRevertToDraft"
              :form-control-enabled="formControlEnabled"
            />
          </div>

          <!-- step 7: Excursion Risk Management Confirmation-->
          <hr class="mt-4 mb-4" />
          <v-row>
            <v-col class="main-title">
              {{ excursionStepperHeadingLabel.EXCURSION_RISK_MANAGEMENT_CONFIRMATION }}
            </v-col>
          </v-row>
          <v-row>
            <v-col class="excursion-builder-i-content">
              <v-icon>fa:fas fa-info-circle</v-icon>
              <span class="confirm-info-text">
                Confirm acknowledgement by reading, agreeing and ticking below.</span
              >
            </v-col>
          </v-row>
          <v-row align-start justify-start class="create-risk-assessment">
            <v-col cols="12">
              <excursion-confirmation
                :disabled-checkbox="isInvalid || !formControlEnabled || archivedExcursionDetected"
                @set-agreement-confirmation-manually-ticked="setAgreementConfirmationManuallyTicked"
              ></excursion-confirmation>
            </v-col>
          </v-row>

          <v-row align="end" justify="end">
            <v-spacer></v-spacer>
            <v-col cols="auto" align-self="end">
              <cs-button
                v-if="excursionApprovalButtonDisplayed"
                id="btnApprove"
                primary
                name="btnApprove"
                :disabled="
                  isInvalid ||
                  !additionalInformationValid ||
                  archivedExcursionDetected ||
                  excursionApprovalToggleActionInProgress ||
                  (approvedExcursionDetected ? formControlEnabled : !formControlEnabled)
                "
                :loading="excursionApprovalToggleActionInProgress"
                label="Approve"
                @click="displayExcursionApprovalToggleDialog()"
              ></cs-button>
              <cs-button
                v-if="excursionUnapprovalButtonDisplayed"
                id="btnUnapprove"
                primary
                name="btnUnapprove"
                :disabled="
                  isInvalid ||
                  !additionalInformationValid ||
                  excursionApprovalToggleActionInProgress ||
                  (approvedExcursionDetected ? formControlEnabled : !formControlEnabled) ||
                  archivedExcursionDetected
                "
                :loading="excursionApprovalToggleActionInProgress"
                label="Undo Approval"
                @click="displayExcursionApprovalToggleDialog()"
              ></cs-button>
              <cs-button
                id="btnSave"
                primary
                :disabled="
                  isInvalid ||
                  !additionalInformationValid ||
                  excursionSaveActionInProgress ||
                  !formControlEnabled ||
                  archivedExcursionDetected
                "
                :loading="excursionSaveActionInProgress"
                label="Save Excursion"
                @click="saveChangesAsDraft()"
              ></cs-button>
              <cs-button
                id="btnClearAndRestart"
                :disabled="!formControlEnabled || archivedExcursionDetected"
                label="Clear and Restart"
                @click="resetConfirmationDialogDisplayed = true"
              ></cs-button>
            </v-col>
          </v-row>
          <hr class="mt-4 mb-4" />
          <v-row>
            <v-col class="main-title">
              {{ excursionStepperHeadingLabel.PREDEPARTURE_AND_POST_EXCURSION }}
            </v-col>
          </v-row>
          <v-row no-gutters align="start" justify="start" class="mt-4 create-risk-assessment">
            <v-col cols="auto" align-self="start">
              <view-predeparture-checklist
                :save-predeparture="submit"
                :form-control-enabled="formControlEnabled"
              ></view-predeparture-checklist>
            </v-col>
            <v-col cols="auto">
              <cs-button
                id="btnIncidentReporting"
                primary
                disabled="true"
                testing-id="btnIncidentReporting"
                label="Incident Reporting"
              ></cs-button>
              <cs-button
                v-if="approvedExcursionDetected && excursionArchivalButtonDiplayed"
                id="btnArchived"
                primary
                testing-id="btnArchived"
                :disabled="archivedExcursionDetected"
                label="Archive Excursion"
                @click="excursionArchiveToggleDialogDisplayed = true"
              ></cs-button>
            </v-col>
            <v-spacer></v-spacer>
          </v-row>
          <!-- Excursion History -->
          <excursion-history v-if="excursionHistoryDisplayed" />
          <cs-form-dialog
            v-if="!presaveConfirmed"
            id="dlg-save-success"
            v-model="successDialogDisplayed"
            body-id="dlg-body-save-success"
            heading="Excursion Status"
            :primary-action="{
              label: 'OK',
              eventName: 'primary-click'
            }"
            @primary-click="confirmSuccess"
          >
            <template #cs-form-dialog-content> Excursion has been saved successfully. </template>
          </cs-form-dialog>
          <cs-form-dialog
            v-if="!presaveConfirmed"
            id="dlg-save-failure"
            v-model="failureDialogDisplayed"
            body-id="dlg-body-save-failure"
            heading="Excursion Status"
            :primary-action="{
              label: 'OK',
              eventName: 'primary-click'
            }"
            @primary-click="failureDialogDisplayed = false"
          >
            <template #cs-form-dialog-content>
              <div v-safe-html="errorMessage"></div>
            </template>
          </cs-form-dialog>
          <cs-form-dialog
            id="dlg-reset-confirmation"
            v-model="resetConfirmationDialogDisplayed"
            body-id="dlg-body-reset-confirmation"
            heading="Reset Excursion"
            :primary-action="{
              label: 'Clear and Restart',
              eventName: 'primary-click'
            }"
            :secondary-action1="{
              label: 'Cancel',
              eventName: 'secondary1-click'
            }"
            @primary-click="clear"
            @secondary1-click="resetConfirmationDialogDisplayed = false"
          >
            <template #cs-form-dialog-content>
              Are you sure you want to clear and restart your excursion?
            </template>
          </cs-form-dialog>
          <cs-form-dialog
            id="dlg-excursion-type-warning"
            v-model="excursionTypeWarningDisplayed"
            body-id="dlg-body-excursion-type-warning"
            heading="Change Excursion Type"
            :primary-action="{
              label: 'OK',
              eventName: 'primary-click'
            }"
            :secondary-action1="{
              label: 'Cancel',
              eventName: 'secondary1-click'
            }"
            @primary-click="clearCategoriesOnExcursionTypeUpdate"
            @secondary1-click="cancelExcursionTypeUpdate"
          >
            <template #cs-form-dialog-content>
              You are about to change your Excursion Type after previously saving your selection.
              Doing so will clear your excursion and you will have to re-enter your details. It will
              keep other information except risk categories.<br /><br />
              Are you sure you want to continue?
            </template>
          </cs-form-dialog>
          <cs-form-dialog
            id="dlg-save-changes-confirmation"
            v-model="changesSaveDialogDisplayed"
            body-id="dlg-body-save-changes-confirmation"
            heading="Save Excursion"
            :primary-action="{
              label: 'Save',
              eventName: 'primary-click'
            }"
            :secondary-action1="{
              label: 'Cancel',
              eventName: 'secondary1-click'
            }"
            @primary-click="saveChangesThenRevertToDraft"
            @secondary1-click="changesSaveDialogDisplayed = false"
          >
            <template #cs-form-dialog-content>
              Changes have been made to this excursion, are you sure you want to save?
            </template>
          </cs-form-dialog>
          <cs-form-dialog
            id="dlg-confirm-approval"
            v-model="excursionApprovalToggleDialogDisplayed"
            body-id="dlg-body-confirm-approval"
            :heading="approvedExcursionDetected ? 'Undo Approval' : 'Approve Excursion'"
            :primary-action="{
              label: approvedExcursionDetected ? 'Undo Approval' : 'Approve',
              eventName: 'primary-click'
            }"
            :secondary-action1="{
              label: 'Cancel',
              eventName: 'secondary1-click'
            }"
            @primary-click="approvedExcursionDetected ? unapproveExcursion() : approveExcursion()"
            @secondary1-click="excursionApprovalToggleDialogDisplayed = false"
          >
            <template #cs-form-dialog-content>
              Are you sure you want to
              {{ approvedExcursionDetected ? 'undo approval for' : 'approve' }}
              this excursion?
            </template>
          </cs-form-dialog>
          <cs-form-dialog
            id="dlg-archive-excursion-confirmation"
            v-model="excursionArchiveToggleDialogDisplayed"
            body-id="dlg-body-archive-excursion-confirmation"
            heading="Archive Excursion"
            :primary-action="{
              label: 'Archive',
              eventName: 'primary-click'
            }"
            :secondary-action1="{
              label: 'Cancel',
              eventName: 'secondary1-click'
            }"
            @primary-click="archiveExcursion"
            @secondary1-click="excursionArchiveToggleDialogDisplayed = false"
          >
            <template #cs-form-dialog-content>
              Archive this excursion? Note this is not reversible.
            </template>
          </cs-form-dialog>
          <cs-form-dialog
            id="dlg-excursion-builder-generic"
            v-model="genericDialogDisplayed"
            body-id="dlg-body-excursion-builder-generic"
            heading="Supervisor & Student Gender Ratios"
            :primary-action="{
              label: 'OK',
              eventName: 'primary-click'
            }"
            @primary-click="genericDialogDisplayed = false"
          >
            <template #cs-form-dialog-content>
              <div v-safe-html="genericDialogMessage"></div>
            </template>
          </cs-form-dialog>
        </form>
      </div>
    </template>
  </cs-view>
</template>

<script>
import * as types from '@/store/mutationTypes';
import { CSBase } from '@complispace/cs-design-system';
import { required, requiredIf } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { mapState } from 'vuex';
import excursionJurisdictions from '../lib/const/excursionJurisdictions';
import excursionStatus from '../lib/const/excursionStatus';
import excursionStepperHeading from '../lib/const/excursionStepperHeading';
import excursionTypeInfo from '../lib/const/excursionTypeInfo';
import trackableEvent from '../lib/const/trackableEvent';
import stbHorizontalLogo from '@/assets/image/StbHorizontalLogo.svg';
import defaultRiskMatrix from '../lib/const/riskMatrixTemplate';
import { populateErrorMessage } from '../lib/errorMessages';
import riskCategoryLabel from '../lib/const/riskCategoryLabel';
import RiskCategory from './RiskCategory';
import ExportExcursionPolicy from './ExportExcursionPolicy';
import PageLoadingAnimation from './PageLoadingAnimation';
import ExcursionConfirmation from './ExcursionConfirmation';
import MandatoryChecklist from './MandatoryChecklist';
import StudentSelection from './StudentSelection';
import StaffStudentSupervision from './StaffStudentSupervision';
import ExcursionHistory from './ExcursionHistory';
import DateSelector from './DateSelector';
import StbIconTooltip from './StbIconTooltip';
import ViewPredepartureChecklist from './ViewPredepartureChecklist';
import UpdateProviders from './UpdateProviders';
import AdditionalInfoList from './AdditionalInfoList';
import RiskRatingOverall from './RiskRatingOverall';
import RiskRatingSummary from './RiskRatingSummary';
import ExportExcursionRiskAssessment from './ExportExcursionRiskAssessment';

export default {
  name: 'ExcursionBuilder',

  components: {
    RiskCategory,
    ExportExcursionPolicy,
    PageLoadingAnimation,
    ExcursionConfirmation,
    MandatoryChecklist,
    StudentSelection,
    StaffStudentSupervision,
    ExcursionHistory,
    DateSelector,
    StbIconTooltip,
    ViewPredepartureChecklist,
    UpdateProviders,
    AdditionalInfoList,
    RiskRatingOverall,
    RiskRatingSummary,
    ExportExcursionRiskAssessment
  },

  extends: CSBase,

  validations() {
    return {
      name: {
        required
      },
      excursionType: {
        required
      },
      vendorExcursionSid: {
        required
      },
      excursionStartDate: {
        required: requiredIf('excursionStartEndDateSetRequired')
      },
      excursionEndDate: {
        required: requiredIf('excursionStartEndDateSetRequired')
      }
    };
  },

  props: {
    propHasParentComponent: Boolean
  },

  setup: () => ({ v$: useVuelidate() }),

  data() {
    return {
      stbHorizontalLogo,
      excursionApprovalToggleDialogDisplayed: false,
      excursionArchiveToggleDialogDisplayed: false,
      successDialogDisplayed: false,
      failureDialogDisplayed: false,
      resetConfirmationDialogDisplayed: false,
      excursionId: 0,
      pdf: {
        title: 'master excursion risk assessment - sample',
        path: 'doc'
      },
      mandatoryChecklistDisplayed: true,
      excursionTypeWarningDisplayed: false,
      originalExcursionType: '',
      additionalInformationValid: true,
      newExcursionType: '',
      excursionSaveActionInProgress: false,
      excursionApprovalToggleActionInProgress: false,
      excursionArchiveToggleActionInProgress: false,
      errorMessage: '',
      presaveConfirmed: false,
      valuesChanged: false,
      firstTimeLoaded: false,
      changesSaveDialogDisplayed: false,
      agreementConfirmationManuallyTicked: false,
      additionalInformationChanged: false,
      excursionStatusChanged: false,
      excursionSetByExcursionChanges: false,
      excursionSetByRiskChanges: false,
      pageRefreshRequired: false,
      jurisdictionTooltip:
        "The content of the destination state should be considered when travelling interstate. Given that we research content across each State and Territory's Department of Education, in some cases there may be additional information to consider even if you are only travelling interstate for a day excursion.",
      genericDialogDisplayed: false,
      genericDialogMessage: '',
      genericDialogTitle: '',
      staffStudentSupervisionDisplayed: true,
      studentSelectionDisplayed: true,
      excursionStartDateMenuDisplayed: false,
      excursionEndDateMenuDisplayed: false,
      excursionCommonAdapter: false,
      checklistAttestationEnabled: false
    };
  },

  computed: {
    ...mapState({
      excursionBuilderButtonDisplayed: (state) => state.common.excursionBuilderButtonDisplayed,
      isInitClientConfigInProgress: (state) => state.authorisation.isInitClientConfigInProgress,
      isAuthenticated: (state) => state.authorisation.isAuthenticated,
      mixedMedicalConditionHash: (state) => state.common.mixedMedicalConditionHash,
      providerUpdateActionDisplayed: (state) => state.common.providerUpdateActionDisplayed,
      excursionArchivalButtonDiplayed: (state) => state.common.excursionArchivalButtonDiplayed,
      vendorExcursionSidInputEnabled: (state) => state.common.vendorExcursionSidInputEnabled,
      excursionAdditionalInformationEnabled: (state) =>
        state.common.excursionAdditionalInformationEnabled,
      excursionHighRiskStudentMedicalEnabled: (state) =>
        state.common.excursionHighRiskStudentMedicalEnabled,
      excursionDisplayReady: (state) => state.common.excursionDisplayReady,
      excursionStudentMedicalConditionsDisplayReady: (state) =>
        state.common.excursionStudentMedicalConditionsDisplayReady,
      excursionLogoDisplayReady: (state) => state.common.excursionLogoDisplayReady,
      clientLogoBackgroundColor: (state) => state.common.clientLogoBackgroundColor,
      vendorClientLogoEnabled: (state) => state.common.vendorClientLogoEnabled,
      excursionTypeLoaded: (state) => state.common.excursionTypeLoaded,
      excursionJurisdictionLoaded: (state) => state.common.excursionJurisdictionLoaded,
      resolvedExcursionStatus: (state) => state.common.excursionDc.status,
      excursionStudentFetchTriggered: (state) => state.common.excursionStudentFetchTriggered,
      excursionRiskOverall: (state) => state.common.excursionDc.riskOverall,
      excursionInherentRiskRatingEnabled: (state) =>
        state.common.excursionInherentRiskRatingEnabled,
      excursionUserRiskRatingEnabled: (state) =>
        state.common.excursionDc.excursionUserRiskRatingEnabled,
      staffToStudentGenderRatioInfoEnabled: (state) =>
        state.common.staffToStudentGenderRatioInfoEnabled,
      mandatoryChecklistBatchConfirmationAgreementEnabled: (state) =>
        state.common.mandatoryChecklistBatchConfirmationAgreementEnabled
    }),
    mandatoryChecklistBatchConfirmationAgreement: {
      get() {
        return this.$store.state.common.excursionDc.mandatoryChecklistBatchConfirmationAgreement;
      },
      set(value) {
        this.$store.commit(
          types.COMMON_SET_MANDATORY_CHECKLIST_BATCH_CONFIRMATION_AGREEMENT,
          value
        );
      }
    },
    name: {
      get() {
        return this.$store.state.common.excursionDc.name || '';
      },
      set(value) {
        this.$store.commit(types.COMMON_SET_EXCURSION_NAME, value);
      }
    },
    excursionType: {
      get() {
        if (this.$store.state.common.excursionDc.typeId) {
          this.setOriginalExcursionType(this.$store.state.common.excursionDc.typeId);
        }

        return this.$store.state.common.excursionDc.typeId;
      },
      async set(value) {
        if (!this.$store.state.common.excursionDc.typeId) {
          this.$store.commit(types.COMMON_SET_EXCURSION_TYPE, value);
          this.originalExcursionType = value;
          if (value) {
            this.$store.commit(types.COMMON_SET_EXCURSION_DC_CATEGORIES, []);

            await this.$store.dispatch('common/getAllRisksByExcursionTypeName', {
              stbApiAdapter: this.$app.stbApiAdapter,
              store: this.$store,
              queryParam: this.$route.query,
              excursionTypeName: value
            });
          }
        } else {
          this.newExcursionType = value;
          this.excursionTypeWarningDisplayed = true;
        }
      }
    },
    jurisdiction: {
      get() {
        return this.$store.state.common.excursionDc.jurisdiction === '' ||
          !this.$store.state.common.excursionDc.jurisdiction
          ? this.$store.state.common.apiDataDefaultJurisdiction
          : this.$store.state.common.excursionDc.jurisdiction;
      },
      set(value) {
        this.$store.commit(types.COMMON_SET_EXCURSION_JURISDICTION, value);
      }
    },
    vendorExcursionSid: {
      get() {
        if (this.vendorExcursionSidInputEnabled) {
          return this.$store.state.common.excursionDc.vendorData.vendorExcursionSid;
        }
        return this.$store.state.common.excursionDc.vendorData.vendorExcursionSid || 0;
      },
      set(value) {
        this.$store.commit(types.COMMON_SET_VENDOR_EXCURSION_ID, value);
      }
    },
    excursionStartDate: {
      get() {
        return this.$store.state.common.excursionDc.startDate;
      },
      set(value) {
        this.$store.commit(types.COMMON_SET_EXCURSION_START_DATE, value);
      }
    },
    formattedExcursionStartDate: {
      get() {
        return this.formatExcursionDate(this.excursionStartDate);
      },
      set(value) {
        return value;
      }
    },
    excursionEndDate: {
      get() {
        return this.$store.state.common.excursionDc.endDate;
      },
      set(value) {
        this.$store.commit(types.COMMON_SET_EXCURSION_END_DATE, value);
      }
    },
    formattedExcursionEndDate: {
      get() {
        return this.formatExcursionDate(this.excursionEndDate);
      },
      set(value) {
        return value;
      }
    },
    excursionTypeChoiceList() {
      if (this.archivedExcursionDetected) {
        return [`${this.$store.state.common.excursionDc.typeId}`];
      }
      return Object.keys(this.$store.state.common.excursionTypeChoiceList);
    },
    approvedExcursionDetected() {
      return excursionStatus.APPROVED === this.resolvedExcursionStatus;
    },
    archivedExcursionDetected() {
      return excursionStatus.ARCHIVED === this.resolvedExcursionStatus;
    },
    jurisdictions() {
      return excursionJurisdictions;
    },
    showJurisdiction() {
      return this.$app.excursionCommon.showJurisdiction(this.$store.state);
    },
    highRiskStudentMedicalConditionsRisks() {
      return this.$store.getters['common/getHighRiskStudentMedicalConditionsRisks'];
    },
    nonHighRiskStudentMedicalConditionsRisks() {
      return this.$store.getters['common/getNonHighRiskStudentMedicalConditionsRisks'];
    },
    isExcursionInitiatedFromPcg() {
      return (
        this.$store.state.common.userAuthSchemeApiParam?.['vendor-sid'] === '10000001' &&
        !!this.$store.state.common.excursionDc.vendorData.vendorExcursionSid &&
        !this.newExcursionDetected
      );
    },
    risks() {
      const { allRisks } = this.$store.state.common;
      return allRisks.filter(
        (r) => r.name !== riskCategoryLabel.HIGH_RISK_STUDENT_MEDICAL_CONDITIONS
      );
    },
    selectErrors() {
      const errors = [];
      if (!this.v$.excursionType.$dirty) return errors;
      if (this.v$.excursionType.required.$invalid) {
        errors.push('Excursion Type is required');
      }
      return errors;
    },
    nameErrors() {
      const errors = [];
      if (!this.v$.name.$dirty) return errors;
      if (this.v$.name.required.$invalid) {
        errors.push('Excursion is required.');
      }

      return errors;
    },
    vendorExcursionSidErrors() {
      const errors = [];

      if (this.vendorExcursionSidInputEnabled) {
        if (!this.v$.vendorExcursionSid.$dirty) return errors;
        if (this.v$.vendorExcursionSid.required.$invalid) {
          errors.push('Excursion Manager ID is required.');
        }
      }

      return errors;
    },
    showError() {
      return this.$store.state.common.showError ? this.$store.state.common.showError : false;
    },
    additionalInformationItems: {
      get() {
        const { additionalInformationItems } = this.$store.state.common.excursionDc;
        const infors = this.$app.lodash.cloneDeep(additionalInformationItems).sort((a, b) => {
          return a.orderId - b.orderId;
        });
        if (infors && infors.length > 0) {
          const inforsAdded = infors.filter((r) => !r.isDeleted);
          if (inforsAdded.length === 1) {
            infors.forEach((r) => {
              const localR = r;
              if (!localR.isDeleted) {
                if (localR.filename || localR.fileSummary || localR.comment) {
                  localR.showDeleteButton = true;
                }
              }
            });
          } else {
            infors.forEach((r) => {
              const localR = r;
              localR.showDeleteButton = true;
            });
          }

          return infors;
        }

        return [
          {
            fileSummary: '',
            comment: '',
            showDeleteButton: false
          }
        ];
      }
    },
    isInvalid() {
      return this.v$.$invalid;
    },
    riskCategoriesValid() {
      return this.$store.getters['common/getSelectedCategoryCount'] > 0;
    },
    stbLogoDisplayed() {
      const { query } = this.$route;

      let stbLogoDisplayed = true;
      if (query && query.referrer && query.referrer.toLowerCase() === 'pcg') {
        stbLogoDisplayed = false;
      }

      return stbLogoDisplayed;
    },
    formControlEnabled() {
      return (
        !(this.resolvedExcursionStatus === excursionStatus.APPROVED) &&
        !(this.resolvedExcursionStatus === excursionStatus.DRAFT)
      );
    },
    buttonUpdateProvidersEnabled() {
      return !(this.resolvedExcursionStatus === excursionStatus.DRAFT);
    },
    excursions() {
      return JSON.parse(JSON.stringify(this.$store.state.common.excursionDc));
    },
    clientTemplate() {
      let { clientTemplate } = this.$store.state.common.excursionDc;
      if (this.excursionId === 0) {
        clientTemplate = this.$store.state.common.clientTemplate;
      }
      return clientTemplate;
    },
    excursionHistoryDisplayed() {
      return this.$store.state.common.excursionDc.id > 0;
    },
    pcgIntegrationDetected() {
      const { query } = this.$route;
      return query && query.referrer && query.referrer.toLowerCase() === 'pcg';
    },
    excursionApprovalButtonDisplayed() {
      const { vendorExcursionSid } = this.$store.state.common.excursionDc.vendorData;
      const { ticked } = this.$store.state.common.excursionDc.agreementConfirmation || {};
      return (
        ticked &&
        !this.pcgIntegrationDetected &&
        !(vendorExcursionSid && parseInt(vendorExcursionSid, 10) > 0) &&
        excursionStatus.APPROVED !== this.resolvedExcursionStatus &&
        this.$store.state.common.excursionApprovalButtonDisplayed
      );
    },
    excursionUnapprovalButtonDisplayed() {
      return (
        excursionStatus.APPROVED === this.resolvedExcursionStatus &&
        !this.pcgIntegrationDetected &&
        this.$store.state.common.excursionUnapprovalButtonDisplayed
      );
    },
    excursionStepperHeadingLabel() {
      return excursionStepperHeading;
    },
    excursionTypeInfoEntryList() {
      return Object.values(excursionTypeInfo);
    },
    studentMedicalConditionsLoadingAnimationDisplayed() {
      return (
        !this.pageLoadingAnimationDisplayed() &&
        this.excursionStudentFetchTriggered &&
        !this.excursionStudentMedicalConditionsDisplayReady
      );
    },
    riskAssessmentNotesDisplay() {
      return this.$store.state.common.excursionDc.riskAssessmentNotesEnabled;
    },
    excursionInherentRiskRatingDetected() {
      const riskOverall = this.excursionRiskOverall;
      const localExcursionInherentRiskRatingDetected =
        this.excursionInherentRiskRatingEnabled &&
        riskOverall &&
        riskOverall.residualRiskRating &&
        riskOverall.inherentRiskRating;

      return localExcursionInherentRiskRatingDetected;
    },
    residualRiskRatingSummaryHeadingLabel() {
      let localResidualRiskRatingSummaryHeadingLabel =
        'CompliSpace Safe Excursions Recommended Rating';

      if (this.excursionInherentRiskRatingDetected) {
        localResidualRiskRatingSummaryHeadingLabel = 'Residual Rating';
      }

      return localResidualRiskRatingSummaryHeadingLabel;
    },
    residualRiskRatingTooltipBubbleText() {
      return this.excursionInherentRiskRatingDetected
        ? 'Residual risk is the level of risk that remains after controls have been applied.'
        : '';
    },
    inherentRiskRatingTooltipBubbleText() {
      return this.excursionInherentRiskRatingDetected
        ? 'Inherent risk is the level of risk that exists where no controls have been applied.'
        : '';
    },
    staffToStudentGenderRatioInfo: {
      get() {
        return this.$store.state.common.excursionDc.staffToStudentGenderRatioInfo;
      },
      set(value) {
        this.$store.commit(types.COMMON_SET_STAFF_TO_STUDENT_GENDER_RATIO_INFO, value);
      }
    },
    riskAssessmentNotes: {
      get() {
        return this.$store.state.common.excursionDc.riskAssessmentNotes;
      },
      set(value) {
        this.$store.commit(types.COMMON_SET_EXCURSION_RISK_ASSESSMENT_NOTES, value);
      }
    },
    getRatioElement() {
      return !!document.getElementById('student-gender-ratios');
    },
    mutipleLineDetected() {
      if (!this.formControlEnabled || this.archivedExcursionDetected) {
        if (this.staffToStudentGenderRatioInfo.length > 160) {
          return true;
        }
        const stringifiedText = JSON.stringify(this.staffToStudentGenderRatioInfo);
        if (stringifiedText.includes('\\n')) {
          return true;
        }
      }
      return false;
    },
    newExcursionDetected() {
      return (
        !this.$store.state.common.excursionDc.id ||
        `${this.$store.state.common.excursionDc.id}` === '0'
      );
    },
    excursionStartEndDateSetEnabled() {
      return (
        this.newExcursionDetected ||
        (!this.newExcursionDetected && this.$store.state.common.excursionStartEndDateSetEnabled)
      );
    },
    excursionStartEndDateSetRequired() {
      return (
        this.excursionStartEndDateSetEnabled &&
        ((typeof this.excursionStartDate !== 'undefined' && this.excursionStartDate !== '') ||
          (typeof this.excursionEndDate !== 'undefined' && this.excursionEndDate !== ''))
      );
    },

    disableDayExcursionEndDate() {
      return excursionTypeInfo?.DAY_EXCURSION.label === this.excursionType;
    }
  },

  watch: {
    excursions(newValue, oldValue) {
      if (
        excursionStatus.SUBMITTED === this.resolvedExcursionStatus &&
        !this.excursionSetByExcursionChanges
      ) {
        const oldExcursion = JSON.parse(JSON.stringify(oldValue));
        const newExcursion = JSON.parse(JSON.stringify(newValue));

        delete oldExcursion.updateSignature;
        delete newExcursion.updateSignature;
        delete oldExcursion.additionalInformationItems;
        delete newExcursion.additionalInformationItems;
        delete oldExcursion.predepartures;
        delete newExcursion.predepartures;
        delete oldExcursion.predepartureChecklistCompleted;
        delete newExcursion.predepartureChecklistCompleted;
        delete oldExcursion.auditList;
        delete newExcursion.auditList;

        if (!this.$app.lodash.isEqual(oldExcursion, newExcursion)) {
          this.valuesChanged = true;
        }
      }
      this.excursionSetByExcursionChanges = false;
    },
    risks() {
      if (this.firstTimeLoaded && !this.excursionSetByRiskChanges) {
        if (excursionStatus.SUBMITTED === this.resolvedExcursionStatus) {
          this.valuesChanged = true;
        }
      } else {
        this.firstTimeLoaded = true;
      }

      this.excursionSetByRiskChanges = false;
    },
    mixedMedicalConditionHash() {},
    excursionStartDate() {
      this.handleExcursionStartEndDateIntegrity();
    }
  },

  beforeUnmount() {
    // @TODO
    // this.$app.eventBus.$off('setExcursionValuesChanged');
  },

  async mounted() {
    this.excursionCommonAdapter = new this.$app.excursionCommon.Adapter({
      store: this.$store,
      app: this.$app
    });
    const that = this;
    const { query, params } = this.$route;
    if (query && query[`excursion-id`]) {
      this.excursionId = query[`excursion-id`];
    }

    const apiQuery = JSON.parse(JSON.stringify(query));

    if (params && params.id) {
      this.excursionId = params.id;
      apiQuery[`excursion-id`] = this.excursionId;
    }
    await this.$nextTick();
    this.validateAgreementConfirmationTick();
    this.updateRiskMatrix(query);

    this.$app.eventBus.$on('setExcursionValuesChanged', this.setExcursionValuesChanged);
    if (!this.propHasParentComponent) {
      const { axiosInstance, eventPluginCommonAdapter, stbApiAdapter } = this.$app;
      const isVendorAuthScheme =
        !!this.$route.query['vendor-sid'] && !!this.$route.query['oauth-signature'];
      const queryParam = isVendorAuthScheme ? this.$route.query : {};
      if (!isVendorAuthScheme) {
        await this.$store.dispatch('authorisation/validateAndSetUserTokenOrLogout', {
          query: this.$route.query,
          axiosInstance
        });
        if (this.$route.name === 'ExcursionBuilderExistingRecord' && params?.id > 0) {
          queryParam['excursion-id'] = params.id;
        }
      }

      await this.$store.dispatch('common/initClientConfig', {
        queryParam,
        eventPluginCommonAdapter,
        stbApiAdapter
      });
      if (
        this.isAuthenticated &&
        !this.excursionBuilderButtonDisplayed &&
        this.$route.name === 'ExcursionBuilderNewRecord'
      ) {
        this.$route.replace('/excursions');
      }
      this.$store.dispatch('common/setLogoImgUrl');

      if (this.isAuthenticated) {
        const getExcursionsArgHash = {
          stbApiAdapter,
          store: this.$store,
          excursionId: that.excursionId,
          query: apiQuery
        };
        await this.$store.dispatch('common/getExcursions', getExcursionsArgHash);
        const argHash = {
          stbApiAdapter,
          store: this.$store,
          queryParam: apiQuery
        };
        await this.$store.dispatch('common/updateProviders', argHash);
        await this.$store.dispatch('common/getExcursionTypes', argHash);
        await this.$store.dispatch('common/getDefaultJurisdiction', argHash);

        const predepartureArgHash = this.$app.lodash.cloneDeep(argHash);
        const genericFrcs = this.excursionCommonAdapter.getRiskCategoryParams();
        const activityFrcs = this.excursionCommonAdapter.getActivityParams();
        // frc stands for: factor risk category
        const frcs = genericFrcs.concat(activityFrcs);

        const excursionTypeName = this.$store.state.common.excursionDc.typeId;
        frcs.push([excursionTypeName]);
        predepartureArgHash.payload = {
          'risk-categories': frcs
        };

        if (frcs) {
          await this.$store.dispatch('common/postPredepartureStrategies', predepartureArgHash);
        }
        // check status
        if (excursionTypeName) {
          await this.$store.dispatch('common/getAllRisksByExcursionTypeName', {
            stbApiAdapter: this.$app.stbApiAdapter,
            store: this.$store,
            queryParam: query,
            excursionTypeName
          });
        }
      }
    }
    this.$nextTick(() => {
      if (this.resolvedExcursionStatus === excursionStatus.APPROVED) {
        this.$app.excursionCommon.enableFormControl('.excursion-builder', false);
      }
    });
  },

  methods: {
    getDownloadFileBtnId(filename) {
      return `excursion-builder--download-file--button-${filename}`;
    },
    formatExcursionDate(date) {
      return this.$app.excursionCommon.formatExcursionDate(date);
    },
    checkIfExcursionStartDateAllowed(date) {
      return this.$app.excursionCommon.checkIfExcursionStartEndDateSetValid({
        excursionStartDate: date,
        excursionEndDate: this.disableDayExcursionEndDate ? '' : this.excursionEndDate,
        excursionType: this.excursionType
      });
    },
    checkIfExcursionEndDateAllowed(date) {
      return this.$app.excursionCommon.checkIfExcursionStartEndDateSetValid({
        excursionStartDate: this.excursionStartDate,
        excursionEndDate: date,
        excursionType: this.excursionType
      });
    },
    handleExcursionStartEndDateIntegrity() {
      let excursionStartDateAllowed = true;
      if (this.excursionStartDate) {
        excursionStartDateAllowed = this.checkIfExcursionStartDateAllowed(this.excursionStartDate);
      }

      let excursionEndDateAllowed = true;
      if (this.excursionEndDate) {
        excursionEndDateAllowed = this.checkIfExcursionEndDateAllowed(this.excursionEndDate);
      }

      if (this.disableDayExcursionEndDate) {
        this.excursionEndDate = this.excursionStartDate;
      } else if (!(excursionStartDateAllowed && excursionEndDateAllowed)) {
        this.excursionEndDate = '';
      }
    },

    toggleStaffStudentSupervision(isShow) {
      this.staffStudentSupervisionDisplayed = isShow;
    },

    toggleStudentSelection(isShow) {
      this.studentSelectionDisplayed = isShow;
    },

    displayDetailsDialog(title, text) {
      const stringifiedText = JSON.stringify(text);
      const showedText = stringifiedText.replaceAll('\\n', '<br/>').replaceAll('"', '');
      this.genericDialogMessage = showedText;
      this.genericDialogTitle = title;
      this.genericDialogDisplayed = true;
    },
    getRiskAssessmentTrackableEventBaseArgHash() {
      const { apiDataDefaultJurisdiction, excursionDc } = this.$store.state.common;
      return this.$app.excursionCommon.getRiskAssessmentTrackableEventBaseArgHash(
        apiDataDefaultJurisdiction,
        excursionDc
      );
    },

    getRiskAssessmentTrackableEventOptionArgHash() {
      return this.$app.excursionCommon.getRiskAssessmentTrackableEventOptionArgHash();
    },

    registerRiskAssessmentBuilderViewUpdateEvent() {
      const trackableEventBaseArgHash = this.getRiskAssessmentTrackableEventBaseArgHash();
      const trackableEventOptionArgHash = this.getRiskAssessmentTrackableEventOptionArgHash();
      this.$app.eventPluginCommonAdapter.registerTrackableEvent(
        trackableEvent.RISK_ASSESSMENT_BUILDER_VIEW_UPDATE,
        trackableEventBaseArgHash,
        trackableEventOptionArgHash
      );
    },

    registerRiskAssessmentExportEvent() {
      const trackableEventBaseArgHash = this.getRiskAssessmentTrackableEventBaseArgHash();
      const trackableEventOptionArgHash = this.getRiskAssessmentTrackableEventOptionArgHash();
      this.$app.eventPluginCommonAdapter.registerTrackableEvent(
        trackableEvent.RISK_ASSESSMENT_EXCURSION_RISK_ASSESSMENT_EXPORT,
        trackableEventBaseArgHash,
        trackableEventOptionArgHash
      );
    },

    registerRiskAssessmentApprovalEvent() {
      const trackableEventBaseArgHash = this.getRiskAssessmentTrackableEventBaseArgHash();
      const trackableEventOptionArgHash = this.getRiskAssessmentTrackableEventOptionArgHash();
      this.$app.eventPluginCommonAdapter.registerTrackableEvent(
        trackableEvent.RISK_ASSESSMENT_APPROVAL,
        trackableEventBaseArgHash,
        trackableEventOptionArgHash
      );
    },

    registerRiskAssessmentUnapprovalEvent() {
      const trackableEventBaseArgHash = this.getRiskAssessmentTrackableEventBaseArgHash();
      const trackableEventOptionArgHash = this.getRiskAssessmentTrackableEventOptionArgHash();
      this.$app.eventPluginCommonAdapter.registerTrackableEvent(
        trackableEvent.RISK_ASSESSMENT_UNAPPROVAL,
        trackableEventBaseArgHash,
        trackableEventOptionArgHash
      );
    },

    registerRiskManifestLinkClickEvent() {
      this.excursionCommonAdapter.registerRiskManifestLinkClickEvent();
    },

    async submit(pdfType, statusChanged, callback) {
      const apiQuery = JSON.parse(JSON.stringify(this.$store.state.common.apiParam));
      const excursionMetadata = JSON.parse(JSON.stringify(this.$store.state.common.excursionDc));
      this.excursionId = excursionMetadata.id;
      delete excursionMetadata.id;
      delete excursionMetadata.additionalInformationItems;
      delete excursionMetadata.validCategories;
      delete excursionMetadata.computed;
      delete excursionMetadata.auditList;
      delete excursionMetadata.selectedLikelihood;
      delete excursionMetadata.selectedConsequence;
      delete excursionMetadata.selectedRating;

      excursionMetadata.name = this.name;
      excursionMetadata.typeId = this.excursionType;
      excursionMetadata.startDate = this.excursionStartDate;
      excursionMetadata.endDate = this.excursionEndDate;

      if (!excursionMetadata.studentMedicalConditionInfo) {
        excursionMetadata.studentMedicalConditionInfo = {};
      }
      excursionMetadata.studentMedicalConditionInfo.mixedMedicalConditionHash =
        this.mixedMedicalConditionHash;

      excursionMetadata.riskOverallUserRating = {
        likelihood: this.$store.state.common.excursionDc.selectedLikelihood,
        consequence: this.$store.state.common.excursionDc.selectedConsequence,
        rating: this.$store.state.common.excursionDc.selectedRating
      };

      excursionMetadata.riskAssessmentNotes = this.riskAssessmentNotes;

      const apiPayload = {
        metadata: excursionMetadata
      };

      if (!pdfType && !callback) {
        this.excursionSaveActionInProgress = true;
      }

      const excursionRiskCombo = await this.excursionCommonAdapter.getExcursionRiskCombo(
        this.$route.query
      );
      if (excursionRiskCombo) {
        apiPayload.metadata.riskOverall = excursionRiskCombo.riskOverall;
        apiPayload.metadata.riskCategories = excursionRiskCombo.riskCategories;
      }

      for (let i = 0; i < this.additionalInformationItems.length; i += 1) {
        this.additionalInformationItems[i].orderId = i + 1;
      }

      await this.insertAdditionalInformationItems(this.additionalInformationItems);

      apiPayload.metadata.additionalInformationNotes = this.additionalInformationItems
        .filter((item) => !item.isDeleted)
        .map((item) => {
          const additionalItem = {
            orderId: item.orderId,
            comment: item.comment,
            fileSummary: item.fileSummary,
            notes: item.notes
          };
          if (item.sid) {
            additionalItem.sid = item.sid;
          }
          return additionalItem;
        });

      if (`${this.excursionId}` !== '0') {
        // set updateSignature
        if (!apiPayload.metadata.updateSignature) {
          apiPayload.metadata.updateSignature = this.$app.uuid.v4();
        }

        const argHash = {
          store: this.$store,
          queryParam: apiQuery,
          id: this.excursionId,
          payload: apiPayload
        };
        const putExcursionsCbResponse = await this.$app.stbApiAdapter.putExcursions(argHash);

        if (
          !this.$store.state.common.isResponseWithError &&
          !pdfType &&
          (!callback || typeof callback !== 'function')
        ) {
          if (!putExcursionsCbResponse.error) {
            this.excursionSetByExcursionChanges = true;
            this.excursionSetByRiskChanges = true;
            this.$store.commit(types.COMMON_SET_EXCURSION, putExcursionsCbResponse);
            this.validateAgreementConfirmationTick();
            if (
              this.valuesChanged &&
              statusChanged &&
              !this.$store.state.common.excursionDc.agreementConfirmation.ticked
            ) {
              this.excursionStatusChanged = true;
              const putExcursionStatusesArgHash = {
                queryParam: apiQuery,
                newStatus: excursionStatus.DRAFT,
                store: this.$store,
                id: this.excursionId
              };
              await this.$app.stbApiAdapter.putExcursionStatuses(putExcursionStatusesArgHash);
            }

            this.excursionSaveActionInProgress = false;
            this.successDialogDisplayed = true;
          } else {
            this.excursionSaveActionInProgress = false;
            this.errorMessage = putExcursionsCbResponse.message;

            this.errorMessage = populateErrorMessage({
              message: this.errorMessage,
              store: this.$store
            });

            this.failureDialogDisplayed = true;
          }
        } else {
          this.excursionSaveActionInProgress = false;
          this.excursionSetByExcursionChanges = true;
          this.excursionSetByRiskChanges = true;
          this.$store.commit(types.COMMON_SET_EXCURSION, putExcursionsCbResponse);
          this.validateAgreementConfirmationTick();
        }

        this.valuesChanged = false;

        if (callback && typeof callback === 'function') {
          this.presaveConfirmed = true;
          await callback();
        }
      } else {
        const argHash = {
          store: this.$store,
          queryParam: apiQuery,
          payload: apiPayload
        };
        const postExcursionsCbResponse = await this.$app.stbApiAdapter.postExcursions(argHash);

        if (!this.$store.state.common.isResponseWithError && !pdfType) {
          if (!postExcursionsCbResponse.error) {
            if (postExcursionsCbResponse.data.id) {
              this.excursionId = postExcursionsCbResponse.data.id;
              this.excursionSetByExcursionChanges = true;
              this.excursionSetByRiskChanges = true;
              this.$store.commit(types.COMMON_SET_EXCURSION, postExcursionsCbResponse);
              this.validateAgreementConfirmationTick();
              const query = JSON.parse(JSON.stringify(this.$route.query));
              this.$router.replace(`/excursions/builder/${this.excursionId}`);

              if (query) {
                this.$router.replace({ query });
              }

              this.excursionSaveActionInProgress = false;
              this.successDialogDisplayed = true;
              this.pageRefreshRequired = true;
            } else {
              this.excursionSaveActionInProgress = false;
              this.successDialogDisplayed = true;
            }
          } else {
            this.excursionSaveActionInProgress = false;
            this.errorMessage = postExcursionsCbResponse.message;

            this.errorMessage = populateErrorMessage({
              message: this.errorMessage,
              store: this.$store
            });
            this.failureDialogDisplayed = true;
          }
        }

        this.valuesChanged = false;
        if (callback && typeof callback === 'function') {
          this.presaveConfirmed = true;
          await callback();
        }
      }

      if (this.excursionSaveActionInProgress) {
        return true;
      }
      return false;
    },

    resetJurisdiction() {
      this.$store.commit(types.COMMON_SET_EXCURSION_JURISDICTION, '');
    },

    handleExcursionTypeSelectionChange() {
      this.resetJurisdiction();
      this.handleExcursionStartEndDateIntegrity();
    },

    clearExcursionStartEndDateSet() {
      this.excursionStartDate = '';
      this.excursionEndDate = '';
    },
    clear() {
      const currentExcursionName = this.name;
      const currentExcursionType = this.excursionType;
      const currentVendorExcursionSid = this.vendorExcursionSid;
      if (!this.isExcursionInitiatedFromPcg) {
        this.clearExcursionStartEndDateSet();
      }

      const updateAdditionalInformationList = this.additionalInformationItems.filter(
        (a) => a.sid && a.sid.length > 0
      );
      updateAdditionalInformationList.forEach((a) => {
        const localA = a;
        localA.isDeleted = true;
      });
      this.updateAdditionalInformationList(updateAdditionalInformationList);

      this.resetConfirmationDialogDisplayed = false;
      this.v$.$reset();

      const excludedAttributeList = [
        'excursionTypeChoiceList',
        'userAuthSchemeApiParam',
        'apiParam',
        'excursionDc',
        'excursionAdditionalInformationEnabled',
        'excursionBuilderButtonDisplayed',
        'excursionCreationButtonDisplayed',
        'excursionDestinationContentEnabled',
        'excursionDisplayReady',
        'excursionHighRiskStudentMedicalEnabled',
        'excursionLogoDisplayReady',
        'excursionPersonResponsibleEnabled',
        'excursionStudentFetchTriggered',
        'excursionStudentMedicalConditionsDisplayReady',
        'logoAttachmentUrl',
        'providerUpdateActionEnabled',
        'excursionSyncButtonDisplayed',
        'userInitials',
        'vendorClientLogoEnabled',
        'vendorExcursionSidInputEnabled',
        'excursionStartEndDateSetEnabled',
        'clientSettingRiskMatrix',
        'clientUsersTabDisplayed',
        'usersTabDisplayed',
        'clientsTabDisplayed',
        'clientSiteSettingsButtonDiplayed',
        'clientSelfGroupManagementDisplayed',
        'publicSchoolFeatureSetEnabled',
        'riskCategoryManagementMenuItemDisplayed',
        'operationManagementMenuItemDisplayed',
        'studentManagementSubjectManagementDisplayed'
      ];
      const argHash = {
        excludedAttributeList,
        storeModuleName: ''
      };
      this.$store.commit(types.COMMON_RESET_STORE_MODULE_STATE, argHash);
      this.$store.commit(types.COMMON_RESET_EXCURSION_STORE_STATE, argHash);
      this.$store.commit(types.COMMON_SET_EXCURSION_STUDENT_MEDICAL_CONDITIONS, []);
      this.$store.commit(types.COMMON_SET_STAFF_TO_STUDENT_GENDER_RATIO_INFO, '');

      const isExcursionInitiatedFromPcg =
        this.$store.state.common.userAuthSchemeApiParam?.['vendor-sid'] === '10000001' &&
        !!currentVendorExcursionSid &&
        !this.newExcursionDetected;

      if (isExcursionInitiatedFromPcg) {
        this.vendorExcursionSid = this.vendorExcursionSidInputEnabled
          ? currentVendorExcursionSid
          : this.vendorExcursionSid;
        this.name = currentExcursionName;
        this.excursionType = currentExcursionType;
      }
      this.validateAgreementConfirmationTick();
    },

    toggleMandatoryChecklist() {
      this.mandatoryChecklistDisplayed = !this.mandatoryChecklistDisplayed;
    },

    confirmSuccess() {
      this.successDialogDisplayed = false;
      if (this.excursionStatusChanged || this.pageRefreshRequired) {
        window.location.reload();
      }
    },

    setOriginalExcursionType(value) {
      this.originalExcursionType = value;
    },

    async clearCategoriesOnExcursionTypeUpdate() {
      this.excursionTypeWarningDisplayed = false;
      this.$store.commit(types.COMMON_SET_EXCURSION_TYPE, this.newExcursionType);
      this.originalExcursionType = this.newExcursionType;
      if (this.newExcursionType) {
        this.handleExcursionStartEndDateIntegrity();

        this.$store.commit(types.COMMON_SET_ALL_RISKS, []);
        this.$store.commit(types.COMMON_SET_EXCURSION_DC_CATEGORIES, []);
        await this.$store.dispatch('common/getAllRisksByExcursionTypeName', {
          stbApiAdapter: this.$app.stbApiAdapter,
          store: this.$store,
          queryParam: this.$route.query,
          excursionTypeName: this.newExcursionType
        });
      }
    },

    cancelExcursionTypeUpdate() {
      this.excursionTypeWarningDisplayed = false;
      this.$store.commit(types.COMMON_SET_EXCURSION_TYPE, '');

      this.$nextTick(() => {
        this.$store.commit(
          types.COMMON_SET_EXCURSION_TYPE,
          this.originalExcursionType ? this.originalExcursionType : ''
        );
      });
    },

    async updateAdditionalInformationList(updateAdditionalInformationList) {
      if (!updateAdditionalInformationList || updateAdditionalInformationList.length === 0) {
        return;
      }

      updateAdditionalInformationList.forEach(async (item) => {
        if (!item.sid || (`${this.excursionId}` !== '0' && !(item.isUpdated || item.isDeleted))) {
          return;
        }
        if (item.isDeleted) {
          this.$refs.additionalInfoList.deleteAttachment(item);
        } else {
          const requestData = {};

          requestData.metadata = {
            summary: item.fileSummary,
            description: item.comment
          };

          if (
            typeof requestData.metadata.summary !== 'undefined' &&
            requestData.metadata.summary.trim() === ''
          ) {
            delete requestData.metadata.summary;
          }

          if (
            typeof requestData.metadata.description !== 'undefined' &&
            requestData.metadata.description.trim() === ''
          ) {
            delete requestData.metadata.description;
          }

          const argHash = {
            queryParam: this.$store.state.common.apiParam,
            excursionId: this.excursionId,
            attachmentSid: item.sid,
            payload: requestData,
            store: this.$store
          };
          await this.$app.stbApiAdapter.putExcursionAttachments(argHash);
        }
      });
    },
    async insertAdditionalInformationItems(additionalInformationItems) {
      const newAdditionalInformationItems = this.$app.lodash.cloneDeep(additionalInformationItems);
      if (newAdditionalInformationItems && newAdditionalInformationItems.length > 0) {
        // add
        const addAdditionalList = newAdditionalInformationItems.filter(
          (a) => typeof a.file !== 'undefined'
        );

        if (addAdditionalList && addAdditionalList.length > 0) {
          // Remove old additional files before adding new files
          const excursionId = this.$store.state.common.excursionDc.id;
          if (excursionId > 0) {
            addAdditionalList.forEach(async (item) => {
              const argHash = {
                queryParam: this.$store.state.common.apiParam,
                excursionId,
                attachmentSid: item.sid,
                store: this.$store
              };
              if (item.sid) {
                await this.$app.stbApiAdapter.deleteExcursionAttachments(argHash);
              }
            });
          }

          addAdditionalList.forEach(async (item) => {
            await this.postNewAdditionalFile(item);
          });
        }

        // update
        const updateAdditionalInformationList = newAdditionalInformationItems.filter(
          (a) => a.sid && a.sid.length > 0
        );

        await this.updateAdditionalInformationList(updateAdditionalInformationList);

        const existAdditions = newAdditionalInformationItems.filter((r) => !r.isDeleted);

        if (existAdditions && existAdditions.length === 0) {
          newAdditionalInformationItems.push({
            fileSummary: '',
            comment: '',
            showDeleteButton: false
          });
        }
        this.$store.commit(types.COMMON_SET_ADDITIONAL_INFORMATIONS, newAdditionalInformationItems);
      }
    },

    async postNewAdditionalFile(additionalItem) {
      const localAdditionalItem = additionalItem;
      const additionalItemList = [];
      const fd = new FormData();
      fd.append(`file-0`, localAdditionalItem.file);
      additionalItemList.push({
        summary: localAdditionalItem.fileSummary,
        description: localAdditionalItem.comment,
        orderId: localAdditionalItem.orderId
      });

      fd.append('items', JSON.stringify(additionalItemList));

      const argHash = {
        queryParam: this.$store.state.common.apiParam,
        excursionId: this.excursionId,
        payload: fd,
        store: this.$store
      };
      const addedRes = await this.$app.stbApiAdapter.postExcursionAttachments(argHash);
      addedRes.data.forEach((a) => {
        const file = a.metadata;
        localAdditionalItem.sid = a.sid;
        localAdditionalItem.comment = file.description;
        localAdditionalItem.filename = file.filename;
        localAdditionalItem.fileSummary = file.summary;
        localAdditionalItem.contentType = file.contentType;
        localAdditionalItem.orderId = file.orderId;
        const fileUpdateDate = new Date(a.created_at);
        if (fileUpdateDate.toString() !== 'Invalid Date') {
          localAdditionalItem.fileUpdateDate = fileUpdateDate.toLocaleString();
        }
      });

      return localAdditionalItem;
    },

    setAdditionalInformationValid(valid) {
      this.additionalInformationValid = valid;
      this.additionalInformationChanged = true;
    },

    clientLogoMarginDisplayed() {
      const { query } = this.$route;
      if (query.referrer || query['vendor-sid'] || query['oauth-signature']) {
        return false;
      }
      return true;
    },

    hasExcursion() {
      if (this.excursionId) {
        if (parseInt(this.excursionId, 10) > 0) {
          return false;
        }
      }
      return true;
    },

    setExcursionValuesChanged() {
      this.valuesChanged = true;
    },

    async saveChangesAsDraft() {
      if (this.valuesChanged || this.additionalInformationChanged) {
        this.additionalInformationChanged = false;
        this.changesSaveDialogDisplayed = true;
      } else {
        this.registerRiskAssessmentBuilderViewUpdateEvent();
        await this.submit(null, true);
      }
    },

    async saveChangesThenRevertToDraft(uncheckExcursionConfirmation) {
      this.registerRiskAssessmentBuilderViewUpdateEvent();
      this.$store.commit(types.COMMON_SET_EXCURSION_DC_STATUS, excursionStatus.DRAFT);
      this.$store.commit(types.COMMON_SET_EXCURSION_STEP, 1);
      this.changesSaveDialogDisplayed = false;
      await this.submit(null, true);
      if (uncheckExcursionConfirmation) {
        this.uncheckExcursionConfirmation();
      }
    },

    setAgreementConfirmationManuallyTicked(flag) {
      this.agreementConfirmationManuallyTicked = flag;
    },

    displayExcursionApprovalToggleDialog() {
      this.excursionApprovalToggleDialogDisplayed = true;
    },
    displayExcursionArchiveToggleDialog() {
      this.excursionArchiveToggleDialogDisplayed = true;
    },

    async unapproveExcursion() {
      const apiQuery = JSON.parse(JSON.stringify(this.$store.state.common.apiParam));
      const putExcursionStatusParams = {
        queryParam: apiQuery,
        newStatus: excursionStatus.SUBMITTED,
        store: this.$store,
        id: this.excursionId
      };
      this.excursionApprovalToggleActionInProgress = true;
      await this.$store.dispatch('common/unapproveExcursion', {
        stbApiAdapter: this.$app.stbApiAdapter,
        putExcursionStatusParams
      });

      this.excursionApprovalToggleActionInProgress = false;
      this.excursionApprovalToggleDialogDisplayed = false;
      this.$app.excursionCommon.enableFormControl('.excursion-builder', true);
      // ensure tickbox is ticked when status changed from approved to submitted
      this.agreementConfirmationManuallyTicked = true;
      const confirmInfo = {
        ...this.$store.state.common.excursionDc.agreementConfirmation,
        ticked: true
      };
      this.$store.commit(types.COMMON_SET_EXCURSION_CONFIRMATION, confirmInfo);
      this.validateAgreementConfirmationTick();
      this.registerRiskAssessmentUnapprovalEvent();
    },

    async approveExcursion() {
      this.excursionApprovalToggleDialogDisplayed = false;
      this.excursionApprovalToggleActionInProgress = true;
      const apiQuery = JSON.parse(JSON.stringify(this.$store.state.common.apiParam));
      const putExcursionStatusesArgHash = {
        queryParam: apiQuery,
        newStatus: excursionStatus.APPROVED,
        store: this.$store,
        id: this.excursionId
      };

      await this.$app.stbApiAdapter.putExcursionStatuses(putExcursionStatusesArgHash);
      this.excursionApprovalToggleActionInProgress = false;
      this.$store.commit(types.COMMON_SET_EXCURSION_DC_STATUS, excursionStatus.APPROVED);
      this.$app.excursionCommon.enableFormControl('.excursion-builder', false);
      this.excursionApprovalToggleDialogDisplayed = false;

      this.registerRiskAssessmentApprovalEvent();
    },

    async archiveExcursion() {
      this.excursionArchiveToggleDialogDisplayed = false;
      this.excursionArchiveToggleActionInProgress = true;
      const apiQuery = JSON.parse(JSON.stringify(this.$store.state.common.apiParam));
      const putExcursionStatusesArgHash = {
        queryParam: apiQuery,
        newStatus: excursionStatus.ARCHIVED,
        store: this.$store,
        id: this.excursionId
      };

      await this.$app.stbApiAdapter.putExcursionStatuses(putExcursionStatusesArgHash);
      this.excursionArchiveToggleActionInProgress = false;
      this.$store.commit(types.COMMON_SET_EXCURSION_DC_STATUS, excursionStatus.ARCHIVED);
      this.$app.excursionCommon.enableFormControl('.excursion-builder', false);
      this.excursionArchiveToggleDialogDisplayed = false;
    },

    async downloadFile(additional) {
      const apiQuery = JSON.parse(JSON.stringify(this.$store.state.common.apiParam));

      const argHash = {
        queryParam: apiQuery,
        sid: additional.sid,
        store: this.$store
      };
      const response = await this.$app.stbApiAdapter.getAttachments(argHash);
      if (response.data) {
        const attachments = response.data;
        if (attachments.length > 0) {
          const file = attachments[0];

          if (file && file.computed && file.metadata) {
            const filePath = file.computed.url.replace(/^\//, '');
            const apiUrl = this.$app.env.API_URL.replace(/\/$/, '');
            const fileUrl = `${apiUrl}/${filePath}`;
            const { filename } = file.metadata;
            this.excursionCommonAdapter.downloadFile(fileUrl, filename, this.$app.deviceDetector);
          }
        }
      }
    },

    pageLoadingAnimationDisplayed() {
      let loadingAnimationDisplayed = false;

      if (this.isInitClientConfigInProgress) {
        loadingAnimationDisplayed = true;
      } else if (
        this.isAuthenticated &&
        (!this.excursionDisplayReady ||
          !this.excursionLogoDisplayReady ||
          this.excursionSaveActionInProgress ||
          this.excursionApprovalToggleActionInProgress)
      ) {
        loadingAnimationDisplayed = true;
      }

      return loadingAnimationDisplayed;
    },

    validateAgreementConfirmationTick() {
      this.$nextTick(() => {
        if (this.$store.state.common.excursionDc.agreementConfirmation.ticked) {
          this.agreementConfirmationManuallyTicked = true;
        } else {
          this.agreementConfirmationManuallyTicked = false;
        }
      });
    },
    async updateRiskMatrix(query) {
      if (this.archivedExcursionDetected) {
        return;
      }
      const cbResponse = await this.$app.stbApiAdapter.getClientRiskMatrix({
        queryParam: query,
        store: this.$store
      });

      if (!cbResponse.error) {
        const {
          data: { likelihood, riskRating, consequence }
        } = cbResponse;
        this.$store.commit(types.COMMON_SET_CLIENT_SETTING_RISK_MATRIX, {
          likelihood,
          riskRating,
          consequence
        });
        this.$store.commit(types.COMMON_SET_RISK_MATRIX_SELF_DEFINED_DETECTED, true);
      } else {
        this.$store.commit(types.COMMON_SET_CLIENT_SETTING_RISK_MATRIX, {
          likelihood: defaultRiskMatrix.likelihood,
          consequence: defaultRiskMatrix.consequence,
          riskRating: defaultRiskMatrix.riskRating
        });
        this.$store.commit(types.COMMON_SET_RISK_MATRIX_SELF_DEFINED_DETECTED, false);
      }
    },
    setChecklistAttestationEnabled(argChecklistAttestationEnabled) {
      this.checklistAttestationEnabled = argChecklistAttestationEnabled;
    },
    uncheckExcursionConfirmation() {
      this.$store.commit(types.COMMON_SET_EXCURSION_CONFIRMATION_TICKED, false);
    }
  }
};
</script>

<style>
.excursion-builder-content {
  min-height: 941px;
  width: 100%;
  max-width: 865px;
  margin: auto;
}

#dlg-body-student-medical-conditions-loading .v-btn {
  display: none;
}

.excursion-builder-remark {
  margin-left: 24px;
  margin-bottom: 24px !important;
}

.link-primary {
  color: rgb(var(--v-theme-primary));
}

.excursion-builder-title-heading {
  margin: 40px 0 0 0;
  color: rgb(0, 0, 0, 0.6);
  font-size: 16px;
}

.excursion-builder-tooltip-content {
  white-space: pre-wrap;
  color: #000000;
}

.excursion-builder-tooltip-button {
  width: auto;
  height: auto;
  margin-left: 8px;
  color: rgb(60, 55, 55, 0.54);
}

.excursion-builder-tooltip-content .v-list__item {
  height: 100%;
  color: black;
  font-size: 12px;
}

.excursion-builder-i-content {
  font-family: 'Gilroy' !important;
}

.excursion-builder-i-content .v-icon {
  color: rgb(60, 55, 55, 0.54);
  padding-bottom: 2px;
  font-size: 16px;
}
.update-provider-stepper-button {
  margin-top: 24px;
  margin-bottom: 12px;
}

.update-provider-stepper-text {
  color: rgb(0, 0, 0, 0.6);
  margin: 12px 0 11px 4px;
}

.excursion-builder-category-section {
  font-size: 1em;
  height: 48px;
  line-height: 48px;
  cursor: pointer;
}

.excursion-builder-category-section .v-icon {
  color: rgb(0, 0, 0, 0.54);
  font-size: 1.5em;
  font-weight: bold;
  padding-bottom: 4px;
}

.excursion-builder-category-section .name {
  font-size: 1.3em;
  font-weight: 500;
  cursor: pointer;
}

.checkbox-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.check-all-label {
  display: inline-block;
  padding-top: 0.4rem;
  font-size: 1rem;
  font-weight: bold;
}

.confirm-info-text {
  color: rgb(0, 0, 0, 0.6);
}

.create-risk-assessment {
  margin-bottom: 20px;
  margin-top: 7px;
}

.mandatory-checklist:hover {
  cursor: pointer;
}
</style>
